/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {LoadingService} from '../../../../shared/loading/loading-service/loading.service';
import {TableModel} from '../../../../shared/table/models/table.model';
import {BusinessSettingsVersionColumnEnum} from '../../models/business-settings-version-column.enum';
import {BusinessSettingsApi} from '../../../../api/ms-settings/business/schema/v1/business-settings.api';
import {TableLineModel} from '../../../../shared/table/models/table-line.model';
import {DatepickerHelperService} from '../../../../shared/utils/datepicker-helper/datepicker-helper.service';
import {
    combineLatest,
    finalize,
    Subscription
} from 'rxjs';
import {EntityTreeDisplayModeEnum} from '../../models/entity-tree-display-mode.enum';
import {TableLineDataModel} from '../../../../shared/table/models/table-line-data.model';
import {BusinessSettingsVersionLineDataModel} from '../../models/business-settings-version-line-data.model';
import {SettingResultModel} from '../../../../api/ms-settings/business/schema/v1/contracts/setting-result.model';
import {SettingSummaryModel} from '../../../../api/ms-settings/business/schema/v1/contracts/setting-summary.model';
import {AppService} from '../../../../app.service';
import {BusinessSettingsMainPageState} from './business-settings-main-page.state';
import {StatefulComponent} from '../../../../shared/stateful/stateful.component';
import * as TablePipes from '../data-pipes/business-settings-main-page-data-pipes';
import {notesPipe} from '../data-pipes/business-settings-main-page-data-pipes';
import {BusinessSettingsModel} from '../../../../api/ms-settings/business/schema/v1/contracts/business-settings.model';
import {AvailableActions} from '../../../../api/ms-settings/business/schema/v1/contracts/available-actions.enum';
import {ToastWrapperService} from '../../../../shared/utils/toast-wrapper-service/toast-wrapper-service';
import {WithPagination} from '../../../../shared/pagination/with-pagination';
import {BusinessSettingsSearchCriteriaService} from '../../services/business-settings-search-criteria.service';
import {BusinessSettingsSubmitModel} from '../../../../api/ms-settings/business/schema/v1/contracts/business-settings-submit.model';
import {ConfirmModalComponent} from '../../../../shared/confirm-modal/confirm-modal/confirm-modal.component';
import {AlertNotificationSeverity} from '../../../../alert-notification-severity.model';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {PathTypeEnum} from '../../../../shared/path-alerts/models/path-type.enum';
import {PathAlertsModel} from '../../../../shared/path-alerts/models/path-alerts.model';
import {PathSummaryModel} from '../../../../api/ms-settings/business/schema/v1/contracts/path-summary.model';
import {PathAlertsState} from '../../../../shared/path-alerts/component/path-alerts.state';
import {PathAlertsEntityModel} from '../../../../shared/path-alerts/models/path-alerts-entity.model';
import {PathModel} from '../../../../shared/path-alerts/models/path.model';
import {UserService} from '../../../../shared/services/user.service';
import {Permission} from '../../../../shared/utils/permission.enum';

@Component({
    selector: 'app-business-settings-main-page',
    templateUrl: './business-settings-main-page.component.html',
    styleUrls: ['./business-settings-main-page.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BusinessSettingsMainPageComponent
        extends StatefulComponent<BusinessSettingsMainPageState>
        implements OnInit, OnDestroy, WithPagination {

    // Event emitters to manually toggle accordion
    @Input() manualOpen = new EventEmitter<void>();
    // Path alerts variables, templates and emitters
    @ViewChild('pathAlertsTemplate') pathAlertsTemplateRef: TemplateRef<any>;
    // Event emitter for the scroll events
    public scrollEvent = new EventEmitter<void>();
    public isToCloseAccordion = new EventEmitter<void>();
    public triggerSetPath = new EventEmitter<PathModel>();
    public triggerEditEntity = new EventEmitter<PathAlertsEntityModel>();
    public triggerUpdateBreadcrumbs = new EventEmitter<void>();
    // Global notes variable
    public notes: string;
    // Full line tooltip detail
    public readonly FULL_LINE_TOOLTIP_DETAIL = 'version line';
    // The table data to be injected in the table component
    public tableData: TableModel<BusinessSettingsVersionColumnEnum, BusinessSettingsVersionLineDataModel> = {
        headers: {
            entries: new Map([
                [
                    BusinessSettingsVersionColumnEnum.VERSION, {
                    code: BusinessSettingsVersionColumnEnum.VERSION,
                    name: 'Version',
                    sortable: false,
                    widthWeight: 14,
                    dataPipe: TablePipes.versionPipe,
                    hasCopyPaste: true
                }
                ],
                [
                    BusinessSettingsVersionColumnEnum.STATUS, {
                    code: BusinessSettingsVersionColumnEnum.STATUS,
                    name: 'Status',
                    sortable: false,
                    widthWeight: 14,
                    hasDot: true,
                    dataPipe: TablePipes.statusPipe,
                    hasCopyPaste: true
                }
                ],
                [
                    BusinessSettingsVersionColumnEnum.ACTIVATION_DATE, {
                    code: BusinessSettingsVersionColumnEnum.ACTIVATION_DATE,
                    name: 'Activation date',
                    sortable: false,
                    widthWeight: 14,
                    dataPipe: TablePipes.activationDatePipe,
                    hasCopyPaste: true
                }
                ],
                [
                    BusinessSettingsVersionColumnEnum.UPDATED_BY, {
                    code: BusinessSettingsVersionColumnEnum.UPDATED_BY,
                    name: 'Updated by',
                    sortable: false,
                    widthWeight: 14,
                    dataPipe: TablePipes.updatedByPipe,
                    hasCopyPaste: true
                }
                ],
                [
                    BusinessSettingsVersionColumnEnum.NOTES, {
                    code: BusinessSettingsVersionColumnEnum.NOTES,
                    name: 'Notes',
                    sortable: false,
                    widthWeight: 14,
                    dataPipe: notesPipe,
                    hasCopyPaste: true
                }
                ],
                [
                    BusinessSettingsVersionColumnEnum.PATH_ALERTS, {
                    code: BusinessSettingsVersionColumnEnum.PATH_ALERTS,
                    name: 'Path Alerts',
                    sortable: false,
                    widthWeight: 30
                }
                ]
            ])
        },
        dynamicSection: [],
        staticSection: [],
        expandableLineTooltipDetail: this.FULL_LINE_TOOLTIP_DETAIL,
        expandableLines: true
    };
    // Available actions map
    private availableActionsMap: Map<number, AvailableActions[]> = new Map<number, AvailableActions[]>();
    // Subscriptions variable
    private subscriptions = new Subscription();

    /**
     * Returns the correct accordion context that is stored in the component state
     */
    get accordionContext(): BusinessSettingsVersionLineDataModel {
        let accordionData = null;
        if (this.state != null && this.state.selectedLine != null) {
            accordionData = this.state.selectedLine.accordionData;
        }
        return accordionData;
    }

    /**
     * Return if the component should show the loading spinner.
     * This is a global spinner that is determined according to the component status itself, but also the tree status.
     */
    get isLoading(): boolean {
        return this.state.isLoading || this.state.tableIsLoading;
    }

    get pathAlertsHasEditButton(): boolean {
        const line = this.state.selectedLine;
        const hasEditAvailableAction = line?.accordionData?.availableActions?.includes(AvailableActions.EDIT) ?? false;
        return hasEditAvailableAction && this.userService.hasPermissions([Permission.ADMIN_BUSINESS_SETTINGS_EDIT]);
    }

    get isViewMode(): boolean {
        const line = this.state.selectedLine;
        return line?.accordionData?.displayMode === EntityTreeDisplayModeEnum.VIEW;
    }

    get pathAlertsHasViewButton(): boolean {
        return this.isViewMode && this.userService.hasPermissions([Permission.ADMIN_BUSINESS_SETTINGS_VIEW]);
    }

    constructor(private loading: LoadingService,
                private businessSettingsApi: BusinessSettingsApi,
                private dataHelper: DatepickerHelperService,
                private appService: AppService,
                private toastService: ToastWrapperService,
                public search: BusinessSettingsSearchCriteriaService,
                public modal: NgbModal,
                private userService: UserService) {
        super();
        this.loading.start();
    }

    hasPagination(): boolean {
        return this.search.totalResults > this.search.pageSize;
    }

    ngOnInit(): void {
        this.subscriptions.add(
                this.appService.onRouterOutletContainerScroll
                        .subscribe(() => {
                            this.scrollEvent.emit();
                        })
        );
        this.retrieveTableData();
    }

    ngOnDestroy(): void {
        this.appService.emitShowBackgroundBlur(false);
        this.subscriptions.unsubscribe();
    }

    changePage = (pageNumber: number): void => {
        this.search.pageNumber = pageNumber;

        this.retrieveTableData();
    };

    /**
     * Retrieves the {@link BusinessSettingsModel} of a specific line {@link TableLineModel} and sets the line.accordionData value
     * @param line the {@link TableLineModel} to which the data must be retrieved
     */
    loadDataTree(line: TableLineModel<BusinessSettingsVersionColumnEnum, BusinessSettingsVersionLineDataModel>): void {
        this.storeState({tableHasBlur: false});
        if (line != null && line.accordionData == null) {
            this.subscriptions.add(this.businessSettingsApi.getSettings(line.version)
                    .subscribe((businessData) => {
                        line.accordionData = {
                            displayMode: EntityTreeDisplayModeEnum.VIEW,
                            title: TablePipes.versionTitleOnExpandableLinePipe(
                                    line.data.get(BusinessSettingsVersionColumnEnum.VERSION).cellValue),
                            titleStatus: TablePipes.statusTitleOnExpandableLine(
                                    line.data.get(BusinessSettingsVersionColumnEnum.STATUS).cellValue),
                            status: line.data.get(BusinessSettingsVersionColumnEnum.STATUS).cellValue,
                            availableActions: this.availableActionsMap.get(line.version),
                            data: businessData,
                            version: line.version,
                            lastChangeDateTime: line.data.get(BusinessSettingsVersionColumnEnum.VERSION).cellValue.lastChangeDateTime,
                            notes: line.data.get(BusinessSettingsVersionColumnEnum.NOTES).cellValue.notes
                        };
                    }));
        } else {
            this.appService.emitShowBackgroundBlur(false);
            this.resetSelectedLinePathAlertsState();
        }

        this.storeState({selectedLine: line});
    }

    /**
     * Applies the changes to the accordion when it is changed to EDIT mode
     */
    changeToEditMode(): void {
        this.appService.emitShowBackgroundBlur(true);
        this.storeState({
            tableHasBlur: true,
            disableAccordion: true,
            selectedLine: {
                ...this.state.selectedLine,
                accordionData: {...this.state.selectedLine.accordionData, displayMode: EntityTreeDisplayModeEnum.EDIT}
            }
        });
    }

    /**
     * Acts after a treeIsSaving event, toggling the table loading indicator.
     * <p> The accordion is only unlocked and the table data reloaded once the save actually succeeds - see {@link onTreeSaved}.
     * @param isSaving If the tree is saving the new version
     */
    treeSaveEvent(isSaving: boolean): void {
        this.storeState({tableIsLoading: isSaving});
    }

    /**
     * Acts after the tree was successfully saved, reloading the table data and releasing the accordion edit lock.
     */
    onTreeSaved(): void {
        this.retrieveTableData(() => this.triggerUpdateBreadcrumbs.emit());
        this.updateTreeStatusAfterDiscardOrSave(EntityTreeDisplayModeEnum.EDIT);
    }

    /**
     * Acts after a treeIsDiscarding event, resetting the state selectedLine to the initial value if the action is starting
     * @param isDiscarding If the tree is discarding the changes
     */
    treeDiscardEvent(isDiscarding: boolean): void {
        this.storeState({
            tableIsLoading: isDiscarding
        });
        if (isDiscarding) {
            this.storeState({
                selectedLine: {...this.state.selectedLine}
            });
        } else {
            this.updateTreeStatusAfterDiscardOrSave(EntityTreeDisplayModeEnum.VIEW);
        }
    }

    /**
     * Acts after a treeWasChanged event, updating the component state
     */
    treeWasChanged(): void {
        this.storeState({disableAccordion: true});
    }

    /**
     * Discard the modal events
     */
    discardModal(): void {
        this.storeState({
            showModal: false,
            tableHasBlur: false
        });
        this.appService.emitShowBackgroundBlur(false);
    }

    isEmpty(array: any[]): boolean {
        return (array?.length ?? 0) === 0;
    }

    /**
     * Dynamically updates the breadcrumbs for the selected line
     * @param updateValue the updated value
     */
    updateLineBreadcrumbs(updateValue: PathAlertsModel[]): void {
        const line = this.retrieveLineByVersion(this.state.selectedLine.version);
        if (line != null) {
            line.data.set(BusinessSettingsVersionColumnEnum.PATH_ALERTS, {
                ...line.data.get(BusinessSettingsVersionColumnEnum.PATH_ALERTS), cellValue: {
                    templateRef: this.pathAlertsTemplateRef,
                    context: {
                        paths: updateValue,
                        selectedSeparator: line.pathAlertsState?.openSeparator
                    }
                }
            });
        }
    }

    /**
     * Updates the path alerts component state in the selectedLine {@link TableLineModel}
     * @param state the updated state
     */
    updatePathAlertsComponentState(state: PathAlertsState): void {
        if (this.state.selectedLine != null) {
            const line = this.retrieveLineByVersion(this.state.selectedLine.version);
            if (line != null) {
                line.pathAlertsState = state;
            }
        }
    }

    editPath(path: PathModel): void {
        if (this.isViewMode) {
            this.changeToEditMode();
        }
        this.triggerSetPath.emit(path);
    }

    viewPath(path: PathModel): void {
        this.triggerSetPath.emit(path);
    }

    editEntity(entity: PathAlertsEntityModel): void {
        if (this.isViewMode) {
            this.changeToEditMode();
        }

        this.triggerEditEntity.emit(entity);
    }

    private resetSelectedLinePathAlertsState(): void {
        if (this.state.selectedLine?.pathAlertsState != null) {
            this.state.selectedLine.pathAlertsState = {
                openEntities: [],
                openSeparator: null
            };
        }
    }

    /**
     * Retrieves a {@link TableLineModel} from the table data, given the corresponding version
     * @param version the version of the line to retrieve
     */
    private retrieveLineByVersion(version: number):
            TableLineModel<BusinessSettingsVersionColumnEnum, BusinessSettingsVersionLineDataModel> {
        let line: TableLineModel<BusinessSettingsVersionColumnEnum, BusinessSettingsVersionLineDataModel> =
                this.tableData.staticSection.find((entry) => entry.version === version);

        if (line == null) {
            line = this.tableData.dynamicSection.find((entry) => entry.version === version);
        }

        return line;
    }

    private convertPathSummaryToPathAlertsModel(pathsSummary: PathSummaryModel): PathAlertsModel[] {
        const pathAlerts: PathAlertsModel[] = [];

        if (pathsSummary.numberOfUnfinishedPaths > 0) {
            pathAlerts.push({
                type: {
                    code: PathTypeEnum.UNFINISHED,
                    name: 'Unfinished path(s)',
                    count: pathsSummary.numberOfUnfinishedPaths
                },
                entities: [], hasAccordion: false
            });
        }

        if (pathsSummary.numberOfInactivePaths > 0) {
            pathAlerts.push({
                type: {
                    code: PathTypeEnum.INACTIVE,
                    name: 'Inactive path(s)',
                    count: pathsSummary.numberOfInactivePaths
                },
                entities: [], hasAccordion: false
            });
        }
        return pathAlerts;
    }

    /**
     * Updates the tree status after a save or discard event, disabling the background blur and setting the display mode as edit or view
     */
    private updateTreeStatusAfterDiscardOrSave(displayModeAfterSaveOrDiscard: EntityTreeDisplayModeEnum): void {
        this.appService.emitShowBackgroundBlur(false);
        this.storeState({
            disableAccordion: false,
            tableHasBlur: false,
            selectedLine: {
                ...this.state.selectedLine,
                accordionData: {
                    ...this.state.selectedLine.accordionData,
                    displayMode: displayModeAfterSaveOrDiscard
                }
            }
        });
    }

    /**
     * Retrieves the table data from the service
     */
    private retrieveTableData(callback?: () => void): void {
        this.storeState({isLoading: true, disableAccordion: false});

        const staticQuery = this.search.getStaticDataQuery();
        const dynamicQuery = this.search.getDynamicDataQuery();

        this.subscriptions.add(combineLatest([
            this.businessSettingsApi.searchSettings(staticQuery),
            this.businessSettingsApi.searchSettings(dynamicQuery)
        ])
                .pipe(finalize(() => {
                    this.loading.stop();
                    if (!this.state.tableIsLoading) {
                        this.storeState({isLoading: false});
                    }
                }))
                .subscribe(([staticSettings, dynamicSettings]) => {
                    this.search.totalResults = dynamicSettings.totalResults;
                    this.tableData.staticSection = this.mapStaticData(staticSettings.settingsPage);
                    this.tableData.dynamicSection = this.mapDynamicData(dynamicSettings.settingsPage);

                    if (staticSettings.settingsPage != null) {
                        staticSettings.settingsPage.forEach((setting) =>
                                this.availableActionsMap.set(setting.version, setting.availableActions));
                    }

                    if (dynamicSettings.settingsPage != null) {
                        dynamicSettings.settingsPage.forEach((setting) =>
                                this.availableActionsMap.set(setting.version, setting.availableActions));
                    }

                    if (this.tableData != null
                            && this.tableData.dynamicSection != null
                            && this.tableData.dynamicSection.length === 0
                            && this.search.pageNumber > 1) {
                        this.changePage(this.search.pageNumber - 1);
                    }

                    if (callback != null) {
                        callback();
                    }
                }));
    }

    /**
     * Maps a {@link SettingSummaryModel} to a Map<BusinessSettingsVersionColumnEnum, TableLineDataModel>
     */
    private mapLineData(data: SettingSummaryModel): Map<BusinessSettingsVersionColumnEnum, TableLineDataModel> {
        this.notes = data.notes;

        return new Map<BusinessSettingsVersionColumnEnum, TableLineDataModel>([
            [
                BusinessSettingsVersionColumnEnum.VERSION, {
                cellValue: {
                    status: data.status,
                    lastChangeDateTime: DatepickerHelperService.convertTimestampToDateAndTime(data.lastChangeDate),
                    version: data.version
                }
            }
            ],
            [
                BusinessSettingsVersionColumnEnum.STATUS, {
                cellValue: {
                    code: data.status.code,
                    description: data.status.description
                },
                dotClass: `status ${data.status.code}`
            }
            ],
            [
                BusinessSettingsVersionColumnEnum.ACTIVATION_DATE, {
                cellValue: {
                    status: data.status,
                    lastChangeDateTime: DatepickerHelperService.convertTimestampToDateAndTime(data.lastChangeDate)
                }
            }
            ],
            [BusinessSettingsVersionColumnEnum.UPDATED_BY, {cellValue: data.user}],
            [
                BusinessSettingsVersionColumnEnum.NOTES, {
                cellValue: {
                    notes: data.notes
                }
            }
            ],
            [
                BusinessSettingsVersionColumnEnum.PATH_ALERTS, {
                cellValue: {
                    templateRef: this.pathAlertsTemplateRef,
                    context: {
                        paths: this.convertPathSummaryToPathAlertsModel(data.pathSummary)
                    }
                }
            }
            ]
        ]);
    }

    private mapDynamicData(settings: SettingSummaryModel[]):
            TableLineModel<BusinessSettingsVersionColumnEnum, BusinessSettingsVersionLineDataModel>[] {
        let lines: TableLineModel<BusinessSettingsVersionColumnEnum, BusinessSettingsVersionLineDataModel>[] = [];

        if (settings != null) {
            lines = settings.map((setting) => ({
                data: this.mapLineData(setting),
                version: setting.version,
                isOpen: false
            } as TableLineModel<BusinessSettingsVersionColumnEnum, BusinessSettingsVersionLineDataModel>));
        }

        return lines;
    }

    /**
     * Populates the static table section with the current {@link SettingSummaryModel} list.
     * @param settings the {@link SettingResultModel} from which to update the array
     */
    private mapStaticData(settings: SettingSummaryModel[]):
            TableLineModel<BusinessSettingsVersionColumnEnum, BusinessSettingsVersionLineDataModel>[] {
        let lines: TableLineModel<BusinessSettingsVersionColumnEnum, BusinessSettingsVersionLineDataModel>[] = [];

        if (settings != null) {
            lines = settings.map((setting) => {
                let line = this.tableData.staticSection.find((entry) => setting.version === entry.version);

                // If not found, initialize as an empty object
                if (line == null) {
                    line = {
                        isOpen: false
                    } as TableLineModel<BusinessSettingsVersionColumnEnum, BusinessSettingsVersionLineDataModel>;
                }

                line.data = this.mapLineData(setting);
                line.version = setting.version;

                if (line.accordionData != null) {
                    line.accordionData.availableActions = setting.availableActions;
                    line.accordionData.title = TablePipes.versionPipe({
                        status: setting.status,
                        lastChangeDateTime: DatepickerHelperService.convertTimestampToDateAndTime(setting.lastChangeDate)
                    });
                    line.accordionData.notes = setting.notes;
                    // At this point it has accordionData which means that this is the selected line, update the state
                    this.storeState({selectedLine: line});
                }

                return line;
            });
        }

        return lines;
    }

    /**
     * Open the Replicate modal
     */
    private openReplicateModal(businessSettingsSubmitData: BusinessSettingsSubmitModel): void {
        const confirmModalInstance: ConfirmModalComponent = this.modal.open(ConfirmModalComponent).componentInstance;
        confirmModalInstance.modalConfiguration = {
            body: {
                width: 420,
                title: 'Are you sure you want to replicate these settings in the current draft version?',
                manualChecks: {
                    title: 'Be aware that:',
                    options: ['This action will update the current draft version with the selected version configurations.']
                }
            },
            title: 'Replicate as Draft version',
            primaryActionTitle: 'Replicate',
            secondaryActionTitle: 'Cancel',
            alertNotificationSeverity: AlertNotificationSeverity.INFORMATION
        };
        confirmModalInstance.primaryEvent.subscribe(() => {
            this.replicate(businessSettingsSubmitData);
        });
    }

    /**
     * Open the Apply modal
     */
    private openApplyModal(businessSettingsSubmitData: BusinessSettingsSubmitModel): void {
        const confirmModalInstance: ConfirmModalComponent = this.modal.open(ConfirmModalComponent).componentInstance;
        confirmModalInstance.modalConfiguration = {
            body: {
                width: 420,
                title: 'Are you sure you want to apply this draft version?',
                manualChecks: {
                    title: 'Be aware that:',
                    options: ['This action will apply all changes between the active version and the draft version']
                },
                notes: {
                    height: 128,
                    placeholder: 'Please, write a note here...',
                    labelNotes: 'Would you like to remark something about this new version? Perhaps a message for ' +
                            'a colleague or just a reminder?',
                    isNotesMandatory: false,
                    maxNumberCharacters: 200
                }
            },
            title: 'Applying draft version',
            primaryActionTitle: 'Apply',
            secondaryActionTitle: 'Discard',
            alertNotificationSeverity: AlertNotificationSeverity.INFORMATION
        };
        confirmModalInstance.primaryEvent.subscribe((notes: string) => {
            businessSettingsSubmitData.notes = notes;
            this.apply(businessSettingsSubmitData);
        });
    }

    /**
     * Apply the changes from Draft Version to Active Version
     */
    private apply(businessSettingsSubmitData: BusinessSettingsSubmitModel): void {
        this.storeState({isLoading: true, disableAccordion: false});
        const subscription = this.businessSettingsApi.applySettings(this.state.selectedLine.version, businessSettingsSubmitData)
                .subscribe(() => {
                    this.retrieveTableData();
                    this.isToCloseAccordion.emit();
                    this.toastService.success('The draft version was successfully applied', 'Success');
                }, () => {
                    this.toastService.error('The draft version was not applied', 'Error');
                });

        subscription.add(() => {
            this.discardModal();
            this.storeState({isLoading: false});
        });

        this.subscriptions.add(subscription);
    }

    /**
     * Replicate Active version to a draft version
     */
    private replicate(businessSettingsSubmitData: BusinessSettingsSubmitModel): void {
        const subscription = this.businessSettingsApi.replicateSettings(this.state.selectedLine.version, businessSettingsSubmitData)
                .subscribe(() => {
                    this.retrieveTableData();
                    this.isToCloseAccordion.emit();
                    this.toastService.success(
                            'The selected version steering table was successfully replicated to draft', 'Success');
                });

        subscription.add(() => {
            this.discardModal();
        });

        this.subscriptions.add(subscription);
    }
}
